import { gsap } from 'gsap'
import { Box3, MathUtils, PerspectiveCamera, Quaternion, Raycaster, Vector2, Vector3 } from 'three'
import type { Object3D } from 'three'
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { AppConfig } from '../config/appConfig'
import type { ExteriorInteractionId, ExteriorScene } from '../scene/ExteriorScene'

export const exteriorGameplayIds = {
  checkDoormat: 'check-elinas-doormat',
  findKey: 'find-elinas-spare-key',
} as const

export type ExteriorGameplayId = typeof exteriorGameplayIds[keyof typeof exteriorGameplayIds]

export interface ExteriorGameplayFeedbackConfig {
  missText?: string
  hint?: {
    afterMisses: number
    text: string
  }
}

type ExteriorFeedbackTone = 'miss' | 'hint'

interface ExteriorGameplayControllerOptions {
  canvas: HTMLCanvasElement
  camera: PerspectiveCamera
  controls: OrbitControls
  scene: ExteriorScene
  config: AppConfig['exterior']['gameplay']
  onRender: () => void
  onWoosh: () => void
  onKeyCollected: () => void
  onFeedback: (
    text: string,
    clientX: number,
    clientY: number,
    tone: ExteriorFeedbackTone,
  ) => void
  onHint: (text: string) => void
  onComplete: (gameplayId: ExteriorGameplayId) => void
}

interface RevealedKeyState {
  flowerpot: Object3D
  doorKey: Object3D
  potHomePosition: Vector3
  potHomeRotationZ: number
  keyHomePosition: Vector3
  keyHomeQuaternion: Quaternion
  keyHomeScale: Vector3
  keyHomeVisible: boolean
}

const isExteriorGameplayId = (value: string): value is ExteriorGameplayId => (
  value === exteriorGameplayIds.checkDoormat || value === exteriorGameplayIds.findKey
)

export class ExteriorGameplayController {
  private readonly canvas: HTMLCanvasElement
  private readonly camera: PerspectiveCamera
  private readonly controls: OrbitControls
  private readonly scene: ExteriorScene
  private readonly config: AppConfig['exterior']['gameplay']
  private readonly onRender: () => void
  private readonly onWoosh: () => void
  private readonly onKeyCollected: () => void
  private readonly onFeedback: ExteriorGameplayControllerOptions['onFeedback']
  private readonly onHint: ExteriorGameplayControllerOptions['onHint']
  private readonly onComplete: (gameplayId: ExteriorGameplayId) => void
  private readonly pointer = new Vector2()
  private readonly raycaster = new Raycaster()
  private activeGameplayId: ExteriorGameplayId | null = null
  private animation: gsap.core.Timeline | null = null
  private hintAnimation: gsap.core.Tween | null = null
  private doormatHomePosition: Vector3 | null = null
  private hintedPotHomePosition: Vector3 | null = null
  private revealedKeyState: RevealedKeyState | null = null
  private controlsEnabledBeforePickup: boolean | null = null
  private actionResolve: (() => void) | null = null
  private feedback: ExteriorGameplayFeedbackConfig = {}
  private missCount = 0
  private hintShown = false
  private busy = false

  public constructor(options: ExteriorGameplayControllerOptions) {
    this.canvas = options.canvas
    this.camera = options.camera
    this.controls = options.controls
    this.scene = options.scene
    this.config = options.config
    this.onRender = options.onRender
    this.onWoosh = options.onWoosh
    this.onKeyCollected = options.onKeyCollected
    this.onFeedback = options.onFeedback
    this.onHint = options.onHint
    this.onComplete = options.onComplete
  }

  public begin(gameplayId: string, feedback: ExteriorGameplayFeedbackConfig = {}): boolean {
    if (!isExteriorGameplayId(gameplayId)) return false
    this.stopHintHighlight()
    this.activeGameplayId = gameplayId
    this.feedback = feedback
    this.missCount = 0
    this.hintShown = false
    this.busy = false
    this.setHoverCursor(false)
    if (gameplayId === exteriorGameplayIds.findKey) this.restoreDoormat()
    return true
  }

  public end(gameplayId: string): void {
    if (this.activeGameplayId !== gameplayId) return
    this.stopHintHighlight()
    this.activeGameplayId = null
    this.feedback = {}
    this.busy = false
    this.setHoverCursor(false)
  }

  public handlePointerMove(clientX: number, clientY: number): void {
    if (!this.activeGameplayId || this.busy) {
      this.setHoverCursor(false)
      return
    }
    const hovered = this.getActiveTargets().some((target) => (
      this.intersects(target, clientX, clientY)
    ))
    this.setHoverCursor(hovered)
  }

  /** Returns true whenever an exterior gameplay gate owns this click. */
  public handleClick(clientX: number, clientY: number): boolean {
    const gameplayId = this.activeGameplayId
    if (!gameplayId) return false
    if (this.busy) return true

    if (gameplayId === exteriorGameplayIds.checkDoormat) {
      const doormat = this.scene.getInteraction('doormat')
      if (!doormat || !this.intersects(doormat, clientX, clientY)) {
        // The first story beat is specifically about checking the remembered
        // hiding place. Wider search feedback/counting starts only after that
        // doormat beat and Bruce's reaction dialogue have finished.
        return true
      }
      this.busy = true
      this.setHoverCursor(false)
      this.animateDoormat(gameplayId)
      return true
    }

    const flowerpot = this.scene.getInteraction('flowerpot')
    if (flowerpot && this.intersects(flowerpot, clientX, clientY)) {
      this.busy = true
      this.setHoverCursor(false)
      this.stopHintHighlight()
      this.animateBrokenPot(gameplayId)
      return true
    }

    const decoyPot = this.scene.getInteraction('decoyPot')
    if (decoyPot && this.intersects(decoyPot, clientX, clientY)) {
      this.busy = true
      this.setHoverCursor(false)
      this.registerMiss(clientX, clientY)
      this.animateDecoyPot()
      return true
    }

    this.registerMiss(clientX, clientY)
    return true
  }

  private getActiveTargets(): Array<NonNullable<ReturnType<ExteriorScene['getInteraction']>>> {
    const ids: ExteriorInteractionId[] = this.activeGameplayId === exteriorGameplayIds.findKey
      ? ['flowerpot', 'decoyPot']
      : ['doormat']
    return ids
      .map((id) => this.scene.getInteraction(id))
      .filter((target): target is NonNullable<typeof target> => Boolean(target))
  }

  private intersects(target: NonNullable<ReturnType<ExteriorScene['getInteraction']>>, clientX: number, clientY: number): boolean {
    const bounds = this.canvas.getBoundingClientRect()
    if (
      bounds.width <= 0
      || bounds.height <= 0
      || clientX < bounds.left
      || clientX > bounds.right
      || clientY < bounds.top
      || clientY > bounds.bottom
    ) return false

    this.pointer.set(
      ((clientX - bounds.left) / bounds.width) * 2 - 1,
      -((clientY - bounds.top) / bounds.height) * 2 + 1,
    )
    this.raycaster.setFromCamera(this.pointer, this.camera)
    return this.raycaster.intersectObject(target, true).length > 0
  }

  private registerMiss(clientX: number, clientY: number): void {
    if (this.activeGameplayId !== exteriorGameplayIds.findKey) return
    this.missCount += 1
    const hint = this.feedback.hint
    if (
      hint
      && !this.hintShown
      && this.missCount >= Math.max(1, Math.round(hint.afterMisses))
    ) {
      this.hintShown = true
      this.onHint(hint.text)
      this.onFeedback(hint.text, clientX, clientY, 'hint')
      this.startHintHighlight()
      return
    }

    this.onFeedback(this.feedback.missText?.trim() || 'Not here...', clientX, clientY, 'miss')
  }

  private animateDecoyPot(): void {
    const decoyPot = this.scene.getInteraction('decoyPot')
    if (!decoyPot) {
      this.busy = false
      return
    }

    const startPosition = decoyPot.position.clone()
    const startRotationZ = decoyPot.rotation.z
    const movement = this.config.decoyPot
    this.animation = gsap.timeline({
      onUpdate: this.onRender,
      onComplete: () => {
        decoyPot.position.copy(startPosition)
        decoyPot.rotation.z = startRotationZ
        this.animation = null
        this.busy = false
        this.onRender()
      },
    })
    this.animation.to(
      decoyPot.position,
      {
        y: startPosition.y + movement.lift,
        duration: movement.duration * 0.42,
        ease: 'power2.out',
      },
      0,
    )
    this.animation.to(
      decoyPot.rotation,
      {
        z: startRotationZ + movement.wiggleRotationZ,
        duration: movement.duration * 0.28,
        ease: 'power2.inOut',
      },
      0,
    )
    this.animation.to(
      decoyPot.rotation,
      {
        z: startRotationZ - movement.wiggleRotationZ * 0.72,
        duration: movement.duration * 0.3,
        ease: 'power2.inOut',
      },
      movement.duration * 0.26,
    )
    this.animation.to(
      decoyPot.rotation,
      {
        z: startRotationZ,
        duration: movement.duration * 0.26,
        ease: 'power2.out',
      },
      movement.duration * 0.52,
    )
    this.animation.to(
      decoyPot.position,
      {
        y: startPosition.y,
        duration: movement.duration * 0.36,
        ease: 'power2.in',
      },
      movement.duration * 0.42,
    )
  }

  private startHintHighlight(): void {
    const flowerpot = this.scene.getInteraction('flowerpot')
    if (!flowerpot || this.hintAnimation) return
    const home = flowerpot.position.clone()
    this.hintedPotHomePosition = home
    this.hintAnimation = gsap.to(flowerpot.position, {
      y: home.y + this.config.hintHighlight.hoverHeight,
      duration: this.config.hintHighlight.duration,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      onUpdate: this.onRender,
    })
  }

  private stopHintHighlight(): void {
    this.hintAnimation?.kill()
    this.hintAnimation = null
    const flowerpot = this.scene.getInteraction('flowerpot')
    if (flowerpot && this.hintedPotHomePosition) {
      flowerpot.position.copy(this.hintedPotHomePosition)
    }
    this.hintedPotHomePosition = null
    this.onRender()
  }

  private animateDoormat(gameplayId: ExteriorGameplayId): void {
    const doormat = this.scene.getInteraction('doormat')
    if (!doormat) {
      this.finish(gameplayId)
      return
    }

    const start = doormat.position.clone()
    this.doormatHomePosition ??= start.clone()
    const movement = this.config.doormat
    this.onWoosh()
    this.animation = gsap.timeline({
      onUpdate: this.onRender,
      onComplete: () => this.finish(gameplayId),
    })
    this.animation.to(
      doormat.position,
      {
        y: start.y + movement.lift,
        duration: 0.18,
        ease: 'power2.out',
      },
      0,
    )
    this.animation.to(
      doormat.position,
      {
        x: start.x + movement.slideBy.x,
        z: start.z + movement.slideBy.z,
        duration: movement.duration,
        ease: 'power2.inOut',
      },
      0.05,
    )
    this.animation.to(
      doormat.position,
      {
        y: start.y + movement.slideBy.y,
        duration: 0.24,
        ease: 'power2.in',
      },
      Math.max(0.1, movement.duration - 0.14),
    )
  }

  private restoreDoormat(): void {
    const doormat = this.scene.getInteraction('doormat')
    const home = this.doormatHomePosition
    if (!doormat || !home || doormat.position.distanceToSquared(home) < 0.000001) return

    this.busy = true
    this.onWoosh()
    this.animation = gsap.timeline({
      onUpdate: this.onRender,
      onComplete: () => {
        this.animation = null
        this.busy = false
        this.onRender()
      },
    })
    this.animation.to(
      doormat.position,
      {
        x: home.x,
        y: home.y + this.config.doormat.lift,
        z: home.z,
        duration: this.config.resetDuration,
        ease: 'power2.inOut',
      },
      0,
    )
    this.animation.to(
      doormat.position,
      {
        y: home.y,
        duration: 0.2,
        ease: 'power2.in',
      },
      Math.max(0.1, this.config.resetDuration - 0.12),
    )
  }

  private animateBrokenPot(gameplayId: ExteriorGameplayId): void {
    const flowerpot = this.scene.getInteraction('flowerpot')
    const doorKey = this.scene.getInteraction('doorKey')
    if (!flowerpot || !doorKey) {
      this.finish(gameplayId)
      return
    }

    const state: RevealedKeyState = {
      flowerpot,
      doorKey,
      potHomePosition: flowerpot.position.clone(),
      potHomeRotationZ: flowerpot.rotation.z,
      keyHomePosition: doorKey.position.clone(),
      keyHomeQuaternion: doorKey.quaternion.clone(),
      keyHomeScale: doorKey.scale.clone(),
      keyHomeVisible: doorKey.visible,
    }
    this.revealedKeyState = state
    doorKey.scale.copy(state.keyHomeScale).multiplyScalar(0.72)
    const movement = this.config.brokenPot

    this.onWoosh()
    this.animation = gsap.timeline({
      onUpdate: this.onRender,
      onComplete: () => this.finish(gameplayId),
    })
    this.animation.to(
      flowerpot.position,
      {
        x: state.potHomePosition.x + movement.moveBy.x,
        y: state.potHomePosition.y + movement.moveBy.y,
        z: state.potHomePosition.z + movement.moveBy.z,
        duration: movement.duration,
        ease: 'power2.inOut',
      },
      0,
    )
    this.animation.to(
      flowerpot.rotation,
      {
        z: state.potHomeRotationZ + movement.tipRotationZ,
        duration: movement.duration,
        ease: 'power2.inOut',
      },
      0,
    )
    this.animation.call(() => {
      doorKey.visible = true
      this.onRender()
    }, [], movement.keyRevealAt)
    this.animation.to(
      doorKey.scale,
      {
        x: state.keyHomeScale.x,
        y: state.keyHomeScale.y,
        z: state.keyHomeScale.z,
        duration: 0.42,
        ease: 'back.out(1.7)',
      },
      movement.keyRevealAt,
    )
  }

  /** Runs only after Bruce's key-found dialogue has been advanced. */
  public collectRevealedKey(): Promise<void> {
    const state = this.revealedKeyState
    if (!state || this.animation) {
      console.warn('The exterior key cannot be collected before its reveal has finished.')
      return Promise.resolve()
    }

    const { flowerpot, doorKey } = state
    const pickup = this.config.keyPickup
    this.controlsEnabledBeforePickup = this.controls.enabled
    this.controls.enabled = false

    this.camera.updateMatrixWorld(true)
    doorKey.updateMatrixWorld(true)
    doorKey.parent?.updateMatrixWorld(true)
    const cameraDirection = this.camera.getWorldDirection(new Vector3())
    const frontWorld = this.camera.position.clone().addScaledVector(
      cameraDirection,
      pickup.frontDistance,
    )
    const cameraRight = new Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion)
    const cameraUp = new Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion)
    const halfHeight = Math.tan(MathUtils.degToRad(this.camera.fov * 0.5)) * pickup.frontDistance
    const halfWidth = halfHeight * this.camera.aspect
    const dockWorld = frontWorld.clone()
      .addScaledVector(cameraRight, halfWidth * pickup.dockOffset.x)
      .addScaledVector(cameraUp, halfHeight * pickup.dockOffset.y)
    const frontLocal = doorKey.parent?.worldToLocal(frontWorld.clone()) ?? frontWorld
    const dockLocal = doorKey.parent?.worldToLocal(dockWorld.clone()) ?? dockWorld

    const desiredWorldQuaternion = new Quaternion().setFromUnitVectors(
      new Vector3(0, 1, 0),
      cameraDirection.clone().negate(),
    )
    const desiredLocalQuaternion = desiredWorldQuaternion.clone()
    if (doorKey.parent) {
      const parentWorldQuaternion = doorKey.parent.getWorldQuaternion(new Quaternion())
      desiredLocalQuaternion.premultiply(parentWorldQuaternion.invert())
    }

    // This asset's root pivot is well above its rendered mesh. Position the
    // visual bounds at screen center and at the achievement node, not the root.
    const visualCenterWorld = new Box3().setFromObject(doorKey).getCenter(new Vector3())
    const visualCenterLocal = doorKey.worldToLocal(visualCenterWorld.clone())
    const frontRootLocal = frontLocal.clone().sub(
      visualCenterLocal.clone()
        .multiply(state.keyHomeScale)
        .multiplyScalar(pickup.frontScale)
        .applyQuaternion(desiredLocalQuaternion),
    )
    const dockRootLocal = dockLocal.clone().sub(
      visualCenterLocal.clone()
        .multiply(state.keyHomeScale)
        .multiplyScalar(0.08)
        .applyQuaternion(desiredLocalQuaternion),
    )
    const dockStart = pickup.flyDuration + pickup.holdDuration

    doorKey.quaternion.copy(desiredLocalQuaternion)
    return new Promise((resolve) => {
      this.actionResolve = resolve
      this.animation = gsap.timeline({
        onUpdate: this.onRender,
        onComplete: () => {
          flowerpot.position.copy(state.potHomePosition)
          flowerpot.rotation.z = state.potHomeRotationZ
          doorKey.visible = false
          doorKey.position.copy(state.keyHomePosition)
          doorKey.quaternion.copy(state.keyHomeQuaternion)
          doorKey.scale.copy(state.keyHomeScale)
          this.revealedKeyState = null
          this.animation = null
          this.restoreControls()
          this.onKeyCollected()
          this.onRender()
          this.finishAction()
        },
      })
      this.animation.to(
        doorKey.position,
        {
          x: frontRootLocal.x,
          y: frontRootLocal.y,
          z: frontRootLocal.z,
          duration: pickup.flyDuration,
          ease: 'power3.inOut',
        },
        0,
      )
      this.animation.to(
        doorKey.scale,
        {
          x: state.keyHomeScale.x * pickup.frontScale,
          y: state.keyHomeScale.y * pickup.frontScale,
          z: state.keyHomeScale.z * pickup.frontScale,
          duration: pickup.flyDuration,
          ease: 'power2.inOut',
        },
        0,
      )
      this.animation.to(
        flowerpot.position,
        {
          x: state.potHomePosition.x,
          y: state.potHomePosition.y,
          z: state.potHomePosition.z,
          duration: this.config.resetDuration,
          ease: 'power2.inOut',
        },
        0.08,
      )
      this.animation.to(
        flowerpot.rotation,
        {
          z: state.potHomeRotationZ,
          duration: this.config.resetDuration,
          ease: 'power2.inOut',
        },
        0.08,
      )
      this.animation.call(this.onWoosh, [], 0.08)
      this.animation.to(
        doorKey.position,
        {
          x: dockRootLocal.x,
          y: dockRootLocal.y,
          z: dockRootLocal.z,
          duration: pickup.dockDuration,
          ease: 'power2.in',
        },
        dockStart,
      )
      this.animation.to(
        doorKey.scale,
        {
          x: state.keyHomeScale.x * 0.08,
          y: state.keyHomeScale.y * 0.08,
          z: state.keyHomeScale.z * 0.08,
          duration: pickup.dockDuration,
          ease: 'power2.in',
        },
        dockStart,
      )
    })
  }

  private finishAction(): void {
    const resolve = this.actionResolve
    this.actionResolve = null
    resolve?.()
  }

  private restoreRevealedKey(): void {
    const state = this.revealedKeyState
    if (!state) return
    state.flowerpot.position.copy(state.potHomePosition)
    state.flowerpot.rotation.z = state.potHomeRotationZ
    state.doorKey.position.copy(state.keyHomePosition)
    state.doorKey.quaternion.copy(state.keyHomeQuaternion)
    state.doorKey.scale.copy(state.keyHomeScale)
    state.doorKey.visible = state.keyHomeVisible
    this.revealedKeyState = null
  }

  private restoreControls(): void {
    if (this.controlsEnabledBeforePickup === null) return
    this.controls.enabled = this.controlsEnabledBeforePickup
    this.controlsEnabledBeforePickup = null
    this.controls.update()
  }

  private finish(gameplayId: ExteriorGameplayId): void {
    this.animation = null
    this.busy = false
    this.onRender()
    this.onComplete(gameplayId)
  }

  private setHoverCursor(hovered: boolean): void {
    this.canvas.dataset.objectHover = hovered ? 'true' : 'false'
  }

  public dispose(): void {
    this.animation?.kill()
    this.animation = null
    this.restoreRevealedKey()
    this.finishAction()
    this.stopHintHighlight()
    this.restoreControls()
    this.activeGameplayId = null
    this.feedback = {}
    this.missCount = 0
    this.hintShown = false
    this.doormatHomePosition = null
    this.busy = false
    this.setHoverCursor(false)
  }
}
